import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { ExternalLink } from "lucide-react"
import If from "../../../if"
import GradientProgressRow from "../../../gradient-progress-row"
import { FourColumnPerformanceSkeleton } from "../../../skeletons"
import StatsPanel from "../../../stats/panel"
import StatsFigure from "../../../stats/figure"
import StatsSegmented from "../../../stats/segmented"
import NoData from "../../../no-data"
import DateFilter, { DateRangePreset } from "../../../activity/date-filter"
import {
  useFetchTopLeadBreakdownTypeEmbedsQuery,
  useFetchTopLeadBreakdownTypeLOsQuery,
  useFetchTopLeadBreakdownTypeURLQuery
} from "../../../../../redux/queries"
import {
  TransformTopLeadBreakdown,
  useTransformTopLeadBreakdown
} from "../../../../hooks"
import { TopLeadBreakdownData } from "../../../../../types"
import { cleanUrl } from "../../../../../common/utils/string"
import { DateTime } from "luxon"

export interface TopLeadBreakdownFilter {
  type: "url" | "embed"
  value: string
}

interface TopLeadBreakdownProps {
  title: string
  range: number
  orgId: string | undefined
  loid: string | undefined
  hasRangeFilter?: boolean
  contentHeight?: string
  hideRangeFilterLabelOnMobile?: boolean
  onSelectFilter?: (filter: TopLeadBreakdownFilter | null) => void
  activeFilter?: TopLeadBreakdownFilter | null
}

export enum TopLeadBreakdownTabs {
  URL = "breakdown-by-url",
  EMBEDS = "breakdown-by-embeds",
  LOS = "breakdown-by-los"
}

const BREAKDOWN_OPTIONS: { value: TopLeadBreakdownTabs; label: string }[] = [
  { value: TopLeadBreakdownTabs.URL, label: "URL" },
  { value: TopLeadBreakdownTabs.EMBEDS, label: "Embed" },
  { value: TopLeadBreakdownTabs.LOS, label: "Loan officer" }
]

const TopLeadBreakdown = ({
  title,
  range,
  orgId,
  loid,
  hasRangeFilter = false,
  contentHeight = "330px",
  hideRangeFilterLabelOnMobile = false,
  onSelectFilter,
  activeFilter
}: TopLeadBreakdownProps) => {
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [tab, setTab] = useState<TopLeadBreakdownTabs>(TopLeadBreakdownTabs.URL)
  const [transformedData, setTransformedData] = useState<
    TransformTopLeadBreakdown[]
  >([])
  const [dateRange, setDateRange] = useState<DateRangePreset>(
    range === 90 ? "last_90" : range === 30 ? "last_30" : "last_7"
  )
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)

  // Convert DateRangePreset to numeric days for API
  const days = useMemo(() => {
    if (!hasRangeFilter) return range
    switch (dateRange) {
      case "today":
        return 1
      case "last_7":
        return 7
      case "last_30":
        return 30
      case "last_90":
        return 90
      case "custom":
        if (customStartDate && customEndDate) {
          const start = DateTime.fromJSDate(customStartDate)
          const end = DateTime.fromJSDate(customEndDate)
          return Math.ceil(end.diff(start, "days").days)
        }
        return 30
      case "all":
      default:
        return 1000
    }
  }, [hasRangeFilter, range, dateRange, customStartDate, customEndDate])

  const previousRange = days * 2

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      setDateRange(range)
      setCustomStartDate(startDate || null)
      setCustomEndDate(endDate || null)
    },
    []
  )

  const fetchParams = (range: number) => ({
    range: range,
    org_id: orgId,
    loid: loid
  })
  const fetchOptions = (canSkip: boolean) => ({
    refetchOnMountOrArgChange: true,
    skip: canSkip
  })

  const { data: urlData, isFetching: isFetchingUrl } =
    useFetchTopLeadBreakdownTypeURLQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.URL)
    )
  const { data: urlPreviousData, isFetching: isFetchingUrlPrevious } =
    useFetchTopLeadBreakdownTypeURLQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.URL)
    )
  const { data: embedsData, isFetching: isFetchingEmbeds } =
    useFetchTopLeadBreakdownTypeEmbedsQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.EMBEDS)
    )
  const { data: embedsPreviousData, isFetching: isFetchingEmbedsPrevious } =
    useFetchTopLeadBreakdownTypeEmbedsQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.EMBEDS)
    )
  const { data: losData, isFetching: isFetchingLOs } =
    useFetchTopLeadBreakdownTypeLOsQuery(
      fetchParams(days),
      fetchOptions(tab !== TopLeadBreakdownTabs.LOS)
    )
  const { data: losPreviousData, isFetching: isFetchingLOsPrevious } =
    useFetchTopLeadBreakdownTypeLOsQuery(
      fetchParams(previousRange),
      fetchOptions(tab !== TopLeadBreakdownTabs.LOS)
    )

  const breakdownData: TopLeadBreakdownData[] | undefined = useMemo(() => {
    if (!isFetching) {
      switch (tab) {
        case TopLeadBreakdownTabs.URL:
          return urlData
        case TopLeadBreakdownTabs.EMBEDS:
          return embedsData
        case TopLeadBreakdownTabs.LOS:
          return losData
        default:
          return []
      }
    }
  }, [urlData, embedsData, losData, isFetching, tab])
  const previousBreakdownData: TopLeadBreakdownData[] | undefined =
    useMemo(() => {
      if (!isFetching) {
        switch (tab) {
          case TopLeadBreakdownTabs.URL:
            return urlPreviousData
          case TopLeadBreakdownTabs.EMBEDS:
            return embedsPreviousData
          case TopLeadBreakdownTabs.LOS:
            return losPreviousData
          default:
            return []
        }
      }
    }, [isFetching, tab, urlPreviousData, embedsPreviousData, losPreviousData])
  const maxCount = useMemo(
    () =>
      transformedData?.length > 0
        ? Math.max(...transformedData.map(data => data.lead_count))
        : 0,
    [transformedData]
  )
  const totalCount = useMemo(
    () =>
      transformedData?.length > 0
        ? transformedData.reduce((sum, data) => sum + data.lead_count, 0)
        : 0,
    [transformedData]
  )
  const currentTransformedData = useTransformTopLeadBreakdown(
    tab,
    breakdownData,
    previousBreakdownData
  )

  useEffect(() => {
    setTransformedData(currentTransformedData)
  }, [currentTransformedData])

  useEffect(() => {
    setIsFetching(
      isFetchingUrl ||
        isFetchingEmbeds ||
        isFetchingLOs ||
        isFetchingUrlPrevious ||
        isFetchingEmbedsPrevious ||
        isFetchingLOsPrevious
    )
  }, [
    isFetchingEmbeds,
    isFetchingEmbedsPrevious,
    isFetchingLOs,
    isFetchingLOsPrevious,
    isFetchingUrl,
    isFetchingUrlPrevious
  ])

  const breakdownSwitch = (
    <StatsSegmented
      label="Break leads down by"
      options={BREAKDOWN_OPTIONS}
      value={tab}
      onChange={setTab}
    />
  )

  return (
    <StatsPanel
      title={title}
      description={
        onSelectFilter
          ? "Where your leads came from. Select a URL or embed to filter the rest of the page."
          : "Where your leads came from"
      }
      actions={
        hasRangeFilter ? (
          <Fragment>
            {breakdownSwitch}
            <DateFilter
              selectedRange={dateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onRangeChange={handleDateRangeChange}
              hideLabelOnMobile={hideRangeFilterLabelOnMobile}
            />
          </Fragment>
        ) : (
          breakdownSwitch
        )
      }
      footer={
        !isFetching && totalCount > 0 ? (
          <dl>
            <StatsFigure
              label="Leads across everything listed"
              value={totalCount.toLocaleString("en-US")}
            />
          </dl>
        ) : undefined
      }
    >
      <If
        condition={isFetching}
        then={<FourColumnPerformanceSkeleton />}
        else={
          <If
            condition={!transformedData || transformedData.length === 0}
            then={<NoData />}
            else={
              <div
                className="min-w-0 overflow-y-auto overflow-x-hidden"
                style={{ height: contentHeight }}
              >
                <div className="grid w-full min-w-0 gap-y-6 py-2">
                  {transformedData.map((breakdownData, index) => {
                    const isUrlTab = tab === TopLeadBreakdownTabs.URL
                    const isEmbedsTab = tab === TopLeadBreakdownTabs.EMBEDS
                    const isClickable =
                      onSelectFilter && (isUrlTab || isEmbedsTab)
                    const value = isUrlTab
                      ? breakdownData.url
                      : breakdownData.name
                    const filterType = isUrlTab ? "url" : "embed"
                    const isActive =
                      activeFilter?.type === filterType &&
                      activeFilter?.value === value

                    return (
                      <GradientProgressRow
                        key={index}
                        count={breakdownData.lead_count}
                        maxCount={maxCount}
                        label={
                          <span className="flex min-w-0 items-center gap-1.5">
                            {isUrlTab && (
                              <ExternalLink
                                size={12}
                                className="shrink-0 text-slate-400"
                              />
                            )}
                            <span className="truncate">
                              {isUrlTab
                                ? cleanUrl(breakdownData.url)
                                : breakdownData.name}
                            </span>
                          </span>
                        }
                        netPercentage={breakdownData.netPercentage}
                        isPositive={breakdownData.isPositive}
                        isActive={isActive}
                        onClick={
                          isClickable && value
                            ? () =>
                                onSelectFilter(
                                  isActive ? null : { type: filterType, value }
                                )
                            : undefined
                        }
                      />
                    )
                  })}
                </div>
              </div>
            }
          />
        }
      />
    </StatsPanel>
  )
}

export default TopLeadBreakdown
